import { useContext, useEffect, useState } from 'react'
import { ReactComponent as Heart } from 'assets/transport/drawer/heart.svg'
import { ReactComponent as Info } from 'assets/transport/drawer/info.svg'
import { ReactComponent as Bottom1 } from 'assets/transport/drawer/bottom1.svg'
import { ReactComponent as Bottom2 } from 'assets/transport/drawer/bottom2.svg'
import { ReactComponent as Bottom3 } from 'assets/transport/drawer/bottom3.svg'
import { ReactComponent as Bottom4 } from 'assets/transport/drawer/bottom4.svg'
import { ReactComponent as Back } from 'assets/transport/drawer/back.svg'
import { ReactComponent as AI } from 'assets/transport/drawer/ai.svg'
import { ReactComponent as Close } from 'assets/header/close.svg'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import { AnimatePresence, motion } from 'framer-motion';
import { useRive } from '@rive-app/react-canvas';
import { GlobalContext } from 'App'
import { ReactTyped } from "react-typed";
import { jsonAxios2 } from 'components/axios'
import ReactMarkdown from "react-markdown";

interface IScoreDrawer {
    isOpen: boolean
    toggleDrawer: () => void
    avgScore: number
    id?: number
}

interface IData {
    punctuality: number,
    documentation: number,
    incident: number,
    payment: number,
    aiSummary: string
}

const ScoreDrawer = ({ isOpen, toggleDrawer, avgScore, id }: IScoreDrawer) => {
    const { isTablet } = useContext(GlobalContext);
    const [color, setColor] = useState<string>('#4AD35E')
    const { rive, RiveComponent } = useRive({
        src: "/beat2.riv", // Rive 파일 경로
        stateMachines: 'State Machine 1',
        autoplay: true,
    });
    const [data, setData] = useState<IData>({} as IData)
    const [bottom, setBottom] = useState([
        {
            popDescription: [
                {
                    title: 'ETA ±1 일 도착 :',
                    score: 15
                },
                {
                    title: 'ETA ±2 일 도착 :',
                    score: 13
                },
                {
                    title: 'ETA 초과 3 일 이상 :',
                    score: 10
                },
                {
                    title: '5일 이상 지연 :',
                    score: 0
                },
            ],
            popTitle: '도착',
            popOpen: false,
            icon: Bottom1,
            label: 'Punctuality',
            description: '정시 도착 평균 점수',
            id: 0,
            score: 0
        },
        {
            popDescription: [
                {
                    title: '운송관리 생성일 + 48시간 내에 서류마감 상태 값 완료 시 : ',
                    score: 15
                },
                {
                    title: '운송관리 생성일 + 28시간 내에 서류마감 상태 값 완료 시 : ',
                    score: 10
                },
                {
                    title: '운송관리 생성일 < 서류마감 완료 상태일 : ',
                    score: 0
                },
            ],
            popTitle: '서류',
            popOpen: false,
            icon: Bottom2,
            label: 'Documentation',
            description: '서류 완성 평균 점수',
            id: 1,
            score: 0
        },
        {
            popDescription: [
                {
                    title: '이슈가 없을 경우 : ',
                    score: 15
                },
                {
                    title: '이슈가 있을 경우 : ',
                    score: 0
                },
            ],
            popTitle: '이슈',
            popOpen: false,
            icon: Bottom3,
            label: 'Incidents',
            description: '이슈 평균 점수',
            id: 2,
            score: 0
        },
        {
            popDescription: [
                {
                    title: 'ETA 기준 +7일내, 운송완료 상태 완료 시 : ',
                    score: 15
                },
                {
                    title: 'ETA 기준 +10일내, 운송완료 상태 완료 시 :',
                    score: 13
                },
                {
                    title: 'ETA 기준 +15일내, 운송완료 상태 완료 시  :',
                    score: 10
                },
                {
                    title: 'ETA 기준 +20일 이상, 운송완료 상태 완료 시 :',
                    score: 0
                },
            ],
            popTitle: '결제',
            popOpen: false,
            icon: Bottom4,
            label: 'Payment',
            description: '결제 평균 점수',
            id: 3,
            score: 0
        },
    ])
    const [typedCompleted, setTypedCompleted] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && id) {
            jsonAxios2.get(`order/${id}/score-detail`).then(res => {
                console.log(res.data)
                if (res.data.code === 200) {
                    setData(res.data.data)
                }
            })
        }
        else {
            setTypedCompleted(false)
        }
    }, [isOpen, id])

    useEffect(() => {
        (avgScore && avgScore > 0 && rive) && updateStatus(avgScore)
    }, [avgScore, rive])

    const updateStatus = (value: number) => {
        if (!value) return;
        if (rive) {
            const inputs = rive.stateMachineInputs("State Machine 1");
            if (inputs) {
                const input = inputs[0];

                if (value > 80) input.value = 0;
                else if (value > 60) input.value = 1;
                else input.value = 2;

                if (value > 80) setColor('#4AD35E');
                else if (value > 60) setColor('#F8A300');
                else setColor('#FF4D4D');
            }
        }
    };

    const convertScore = (idx: number): number => {
        switch (idx) {
            case 0: return data.punctuality || 0
            case 1: return data.documentation || 0
            case 2: return data.incident || 0
            case 3: return data.payment || 0
            default: return 0
        }
    }

    const handleComplete = () => {
        setTypedCompleted(true);
    };

    return (
        <Drawer
            open={isOpen}
            onClose={() => {
                toggleDrawer();
                setBottom(prev =>
                    prev.map(b => ({
                        ...b,
                        popOpen: false,
                    }))
                )
            }}
            direction='left'
            lockBackgroundScroll
            size={isTablet ? '100%' : `750px`}
        >
            <div className='flex flex-col gap-10 p-10 tablet:p-4 h-full overflow-y-auto'>
                <div className='flex justify-between items-start'>
                    <div
                        className={`flex flex-col items-center justify-center gap-1 p-3 border rounded`}
                        style={{ borderColor: color }}
                    >
                        <div className='leading-none'>
                            <p className='font-semibold text-sm'>
                                PULSE SCORE
                            </p>
                            <p className='text-[#9D9EB7] text-xs'>
                                for the Current Status
                            </p>
                        </div>

                        <div className='flex items-center gap-0.5'>
                            <p
                                className={`digital text-60 leading-none`}
                                style={{ color: color }}
                            >
                                {avgScore}
                            </p>
                            <div className='flex flex-col justify-between items-center gap-0.5 text-[#9D9EB7]'>
                                <Heart fill={color} />
                                <p className='text-lg leading-none'>BPM</p>
                            </div>
                        </div>

                        <RiveComponent
                            height={30}
                            width={140}
                            className='w-35 h-7.5'
                        />
                    </div>
                    <Back onClick={toggleDrawer} />
                </div>
                <div className='flex flex-col flex-1 gap-2'>
                    <div className='flex items-center gap-2.5'>
                        <AI />
                        <div className='leading-none'>
                            <p className='font-semibold'>AI 요약</p>
                            <p className='text-[#9D9EB7] text-xs'>AI를 통한 유저 물류 데이터 분석</p>
                        </div>
                    </div>

                    {
                        isOpen &&
                        <div
                            className='flex-1 p-3 border rounded font-semibold leading-relaxed whitespace-pre-line'>
                            {
                                !data.aiSummary &&
                                '데이터가 없습니다.'
                            }
                            {(!typedCompleted && data.aiSummary) ? (
                                <ReactTyped
                                    strings={[data.aiSummary]}
                                    typeSpeed={5}
                                    showCursor={false}
                                    onComplete={() => handleComplete()}
                                />
                            ) : (
                                <ReactMarkdown
                                    components={{
                                        code({ node, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || "");
                                            return match ? (
                                                <pre className="bg-black my-3 p-3 rounded text-white">
                                                    <code className="text-sm" {...props}>
                                                        {children}
                                                    </code>
                                                </pre>
                                            ) : (
                                                <code className="px-1 rounded text-red text-sm" {...props}>
                                                    {children}
                                                </code>
                                            );
                                        },
                                    }}
                                >
                                    {data.aiSummary}
                                </ReactMarkdown>
                            )}
                        </div>
                    }
                </div>

                <div className='flex tablet:flex-col items-center gap-3 text-xs'>
                    {
                        bottom.map((item, idx) => (
                            <div className='relative flex flex-col flex-1 gap-6 p-3 border border-[#9D9EB7] rounded tablet:w-full' key={idx}>
                                <div className='flex justify-between gap-1'>
                                    <div className='flex gap-1'>
                                        <item.icon />
                                        <div className='leading-none'>
                                            <p className='mb-0.5 font-semibold'>{item.label}</p>
                                            <p className='text-[#9D9EB7]'>{item.description}</p>
                                        </div>
                                    </div>
                                    <Info
                                        className='z-50 relative cursor-pointer'
                                        onClick={() => {
                                            setBottom(prev => prev.map((b, i) => i === idx ? { ...b, popOpen: !b.popOpen } : { ...b, popOpen: false }))
                                        }}
                                    />
                                </div>

                                <div
                                    className='font-semibold text-40 text-right leading-10'
                                    style={{ color: color }}
                                >
                                    +{convertScore(idx)}
                                </div>
                                <AnimatePresence>
                                    {
                                        item.popOpen &&
                                        <motion.div
                                            className={`absolute border border-black rounded bottom-[105%] w-[310px] bg-white p-4.5 z-50 right-0 ${idx < 2 ? '2xl:left-0' : '2xl:right-0'}`}
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <Close
                                                className='top-4.5 right-4.5 absolute w-4.5 h-4.5 cursor-pointer'
                                                onClick={() => {
                                                    setBottom(prev => prev.map((b, i) => i === idx ? { ...b, popOpen: false } : b))
                                                }}
                                            />
                                            <p className='font-semibold'>점수 산정 방식</p>
                                            <p className='mb-6 font-semibold text-[#9D9EB7]'>
                                                {item.popTitle} · {item.label}
                                            </p>

                                            <div className='flex flex-col gap-3'>
                                                {
                                                    item.popDescription &&
                                                    item.popDescription.map((desc, i) => (
                                                        <div className='flex items-center gap-2 font-semibold' key={i}>
                                                            <p className='flex justify-center items-center bg-primary rounded-full w-4.5 h-4.5 text-white text-center'>
                                                                {i + 1}
                                                            </p>
                                                            <p className='flex-1'>{desc.title}</p>
                                                            <p className='text-[#4AD35E]'>{desc.score}점</p>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </motion.div>
                                    }
                                </AnimatePresence>

                                {
                                    item.popOpen &&
                                    <div
                                        className='top-0 left-0 z-10 fixed w-[750px] h-full'
                                        onClick={() => {
                                            setBottom(prev => prev.map((b, i) => i === idx ? { ...b, popOpen: false } : b))
                                        }}
                                    ></div>
                                }
                            </div>
                        ))
                    }
                </div>
            </div>
        </Drawer>
    )
}

export default ScoreDrawer;